import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useIntersectionObserver } from "usehooks-ts"

type UnreadSeparatorProps = {
  unreadCount: number
  onHide: () => void
}

const UnreadSeparator = ({ unreadCount, onHide }: UnreadSeparatorProps) => {
  const { t } = useTranslation()
  const timerRef = useRef<NodeJS.Timeout>()
  const [isFading, setIsFading] = useState(false)

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
    freezeOnceVisible: true,
  })

  // Set up IntersectionObserver to detect when separator becomes visible
  useEffect(() => {
    if (isIntersecting && !timerRef.current) {
      timerRef.current = setTimeout(() => {
        setIsFading(true)
      }, 10000) // 10 seconds
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [isIntersecting])

  // Handle fade out completion
  useEffect(() => {
    if (isFading) {
      const fadeTimeout = setTimeout(() => {
        onHide()
      }, 300) // Match the transition duration

      return () => clearTimeout(fadeTimeout)
    }
  }, [isFading, onHide])

  return (
    <div
      ref={ref}
      className={`my-4 flex items-center gap-4 px-4 transition-opacity duration-300 ${
        isFading ? "opacity-0" : "opacity-100"
      }`}
    >
      {/* Left line */}
      <div className="h-px flex-1 bg-base-primary" />

      {/* Label */}
      <span className="text-sm font-medium text-base-primary">
        {t("chat.unreadSeparator.newMessages", { count: unreadCount })}
      </span>

      {/* Right line */}
      <div className="h-px flex-1 bg-base-primary" />
    </div>
  )
}

export default UnreadSeparator
